/**
 * Carrying the Filter_Context from one screen to another
 * (`complan-governance` Requirement 6 criteria 3, 5, 9).
 *
 * R6.3: *"WHEN a user navigates from one screen applying the Filter_Context to
 * another, THE ComPlan SHALL carry the filter selection, the planning year and the
 * sort order across, and SHALL apply the arrived-at screen's own defaults only for
 * values the Filter_Context does not carry."*
 *
 * Before this module the sidebar's targets were constant strings - `/calendar`,
 * `/initiatives` - so navigating away from a filtered screen silently discarded
 * every dimension the user had set. That is the criterion's whole subject, and a
 * constant link cannot satisfy it.
 *
 * ## What travels, and what deliberately does not
 *
 * Three things travel, exactly as the criterion names them: the filter dimensions,
 * the planning year, and the sort order.
 *
 * What does not travel is each screen's *own* view state, declared per screen as
 * {@link ScreenDescriptor.ownParams}. The list's page window is the clearest case:
 * carrying `offset=40` onto a screen showing four rows lands the user on an empty
 * page that reads as "no matches", which is the failure
 * `initiative-list-search-params.ts` already guards against on a filter change. The
 * calendar's orientation is the same kind of value - layout, not selection - and a
 * dashboard has no orientation to receive it.
 *
 * The distinction is not "which params exist" but "which params are the *selection*".
 * Only the selection is shared state; the rest belongs to the screen that renders it.
 *
 * ## Where the planning year lives differs by screen, and that is the point
 *
 * The Calendar_View holds the year in its route path (`/calendar/:year`, Foundation
 * R11.13 - it is a linkable screen coordinate). The dashboards and the Initiative
 * list have no such segment, so for them the year is a query param. A screen
 * therefore declares {@link ScreenDescriptor.yearIn}, and this module puts the year
 * where the target expects to read it. A caller never has to know.
 *
 * That the Initiative list *carries* a year it does not filter by is intentional and
 * worth stating, because it looks like a mistake. The list is deliberately not
 * year-scoped (see its own doc). But R6.3 requires the year to survive a
 * navigation, and a user going calendar 2026 → list → dashboard must arrive at the
 * dashboard in 2026, not in the current system year. So the list carries the year
 * through without applying it. A screen that drops a value it does not personally
 * use breaks the chain for every screen after it.
 *
 * ## Absence is how the target's defaults get applied
 *
 * The criterion's second half needs no mechanism of its own. Every screen codec
 * reads an absent param as "unfiltered" and lets its schema default decide, so this
 * module satisfies "apply the arrived-at screen's defaults only for values the
 * Filter_Context does not carry" by *writing only what the context carries* and
 * inventing nothing. There is no defaults table here, and there must not be one:
 * a second opinion about a screen's default is how two screens start disagreeing.
 */
import type { InitiativeFilter } from '@complan/contracts';
import {
  parseFilterDimensions,
  writeFilterDimensions,
  type ListFilterKey,
  type BooleanFilterKey,
} from './filter-search-params';

/** The screens that share a Filter_Context (R6.1's participating set). */
export type FilterScreen =
  | 'calendar'
  | 'initiative-list'
  | 'governance-dashboard'
  | 'management-dashboard';

/** Where a screen reads the planning year from. */
export type YearLocation = 'path' | 'query';

/** How one participating screen carries and receives a Filter_Context. */
export interface ScreenDescriptor {
  readonly screen: FilterScreen;
  readonly yearIn: YearLocation;
  /**
   * The screen's own view state: params that stay behind when the user leaves.
   *
   * Not a list of everything the screen reads - the filter dimensions are shared and
   * are never listed here. This is the layout-and-window state that would be
   * meaningless or harmful on another screen. See this module's header.
   */
  readonly ownParams: readonly string[];
  /** The path for this screen, given the year the context carries (if any). */
  readonly buildPath: (year: number | undefined) => string;
}

/**
 * Every participating screen.
 *
 * The two dashboards are registered before their routes exist, because the
 * Drill_Through of R3.8 and R4.9 builds links *to* the Initiative list *from* them
 * and needs their descriptors either way. A descriptor is a statement about how a
 * screen carries state, not a claim that it is reachable.
 */
export const FILTER_SCREENS: Readonly<Record<FilterScreen, ScreenDescriptor>> = {
  calendar: {
    screen: 'calendar',
    yearIn: 'path',
    // Orientation is layout (month versus Sales_Cycle); the sort travels, so it is
    // not listed here.
    ownParams: ['orientation'],
    // A context with no year goes to the year-less path, which redirects to the
    // current planning year - the same entry a first visit gets.
    buildPath: (year) => (year === undefined ? '/calendar' : `/calendar/${String(year)}`),
  },
  'initiative-list': {
    screen: 'initiative-list',
    yearIn: 'query',
    // The page window. Carrying an offset across a screen change is how a user
    // lands on an empty page; see this module's header.
    ownParams: ['limit', 'offset'],
    buildPath: () => '/initiatives',
  },
  'governance-dashboard': {
    screen: 'governance-dashboard',
    yearIn: 'query',
    // The cut-off date is the dashboard's own evaluation coordinate (R3.12), not a
    // filter dimension, so it does not follow the user to another screen.
    ownParams: ['cutOffDate'],
    buildPath: () => '/governance',
  },
  'management-dashboard': {
    screen: 'management-dashboard',
    yearIn: 'query',
    ownParams: ['cutOffDate'],
    buildPath: () => '/dashboard',
  },
};

/** The sort order, as the two params every sorting screen spells it with. */
export interface CarriedSort {
  readonly field: string;
  readonly direction: string;
}

/**
 * The shared selection, extracted from one screen's URL.
 *
 * `dimensions` holds only filter params - never the source screen's own view state -
 * so handing it to a target cannot import a value the target has no business
 * receiving. `planningYear` and `sort` are `undefined` when the source did not carry
 * them, which is what lets the target apply its own default rather than one invented
 * here.
 */
export interface FilterContext {
  readonly planningYear: number | undefined;
  readonly dimensions: URLSearchParams;
  readonly sort: CarriedSort | undefined;
}

/**
 * Reads the Filter_Context out of the URL a screen is currently showing.
 *
 * `pathYear` is supplied by the caller because only the routing layer knows it -
 * for a `yearIn: 'path'` screen it is the `:year` segment. It wins over any
 * `planningYear` query param, since for that screen the path *is* the year.
 */
export function readFilterContext(input: {
  readonly screen: FilterScreen;
  readonly search: URLSearchParams;
  readonly pathYear?: number | undefined;
}): FilterContext {
  const descriptor = FILTER_SCREENS[input.screen];

  const dimensions = new URLSearchParams();
  // Only the dimensions the shared codec recognises, so a screen's own params and
  // any stray query junk are left behind rather than travelling.
  const recognised = parseFilterDimensions(input.search);
  for (const [key, value] of Object.entries(recognised)) {
    if (Array.isArray(value)) {
      for (const entry of value) {
        dimensions.append(key, String(entry));
      }
    } else {
      dimensions.set(key, String(value));
    }
  }

  const sortField = input.search.get('sortField');
  const sortDirection = input.search.get('sortDirection');

  return {
    planningYear: readYear(descriptor, input.search, input.pathYear),
    dimensions,
    // Carried only when the source stated it. A screen sitting on its own default
    // sort states nothing, and the target then applies *its* default - which is
    // R6.3's second half, and why a partial sort is not completed here.
    sort:
      sortField !== null && sortDirection !== null
        ? { field: sortField, direction: sortDirection }
        : undefined,
  };
}

function readYear(
  descriptor: ScreenDescriptor,
  search: URLSearchParams,
  pathYear: number | undefined,
): number | undefined {
  if (descriptor.yearIn === 'path' && pathYear !== undefined) {
    return pathYear;
  }
  const raw = search.get('planningYear');
  if (raw === null) {
    return pathYear;
  }
  const parsed = Number.parseInt(raw, 10);
  // A nonsense year is dropped rather than propagated, so the target falls back to
  // its own default instead of requesting an impossible planning year.
  return Number.isInteger(parsed) ? parsed : pathYear;
}

/**
 * The search string for `target`, carrying `context`.
 *
 * Writes the year only where the target reads it from the query, so a calendar
 * target gets it in the path instead and never in both places - two spellings of one
 * value is how they start to disagree.
 */
export function filterContextSearch(
  target: FilterScreen,
  context: FilterContext,
): URLSearchParams {
  const descriptor = FILTER_SCREENS[target];
  const params = new URLSearchParams(context.dimensions);

  if (descriptor.yearIn === 'query' && context.planningYear !== undefined) {
    params.set('planningYear', String(context.planningYear));
  }
  if (context.sort !== undefined) {
    params.set('sortField', context.sort.field);
    params.set('sortDirection', context.sort.direction);
  }
  return params;
}

/** The href for `target`, carrying `context`: a path plus the search string. */
export function filterContextHref(target: FilterScreen, context: FilterContext): string {
  const descriptor = FILTER_SCREENS[target];
  const path = descriptor.buildPath(context.planningYear);
  const search = filterContextSearch(target, context).toString();
  return search === '' ? path : `${path}?${search}`;
}

/**
 * A Filter_Context built from a parsed filter rather than from a URL.
 *
 * This is the Drill_Through direction (R3.8, R4.9, R6.4): a dashboard holds a parsed
 * `InitiativeFilter` and needs a link to the Initiative list showing exactly the
 * records behind a figure. Serialising through the shared codec is what makes R6.9's
 * "the four cannot describe different record sets" structural - the link is built
 * from the same selection the metric was computed from, not from a second reading of
 * the screen's controls.
 */
export function filterContextFromFilter(
  filter: Pick<InitiativeFilter, ListFilterKey | BooleanFilterKey | 'search' | 'planningYear'>,
  sort?: CarriedSort,
): FilterContext {
  const dimensions = new URLSearchParams();
  writeFilterDimensions(dimensions, filter);
  return { planningYear: filter.planningYear, dimensions, sort };
}
